import { useCallback, useMemo } from "react";

import type { Point, Size } from "../types";
import { ConversionContext, restrictPointToBounds } from "./ConversionContext";
import { useDimensions } from "./DimensionsContext";
import { usePan } from "./PanContext";
import { useZoom } from "./ZoomContext";

/*
  This provider exposes utility functions to map coordinates between the following references:
  - "client" is the browser viewport
  - "map" is the module map
  - "content" is the module map's container

  TODO: add new functions when needed
*/

// How far the map's origin sits from the top left of the perspective viewport, in client pixels.
const getPerspectiveOffset = ({
  mapSize,
  viewportSize,
  positionOrigin,
  scaledPan,
  zoom,
}: {
  mapSize: Size;
  viewportSize: Size;
  positionOrigin: Point;
  scaledPan: Point;
  zoom: number;
}): Point => ({
  x: (mapSize.width / 2 - positionOrigin.x) * zoom - (viewportSize.width / 2 + scaledPan.x),
  y: (mapSize.height / 2 - positionOrigin.y) * zoom - (viewportSize.height / 2 + scaledPan.y),
});

const useConversionState = () => {
  const { viewportSize, viewportRef, width, height, moduleMapBounds, positionOrigin } = useDimensions();
  const { zoom } = useZoom();
  const { scaledPan } = usePan();

  const perspectiveOffset = useMemo(
    () =>
      viewportSize &&
      getPerspectiveOffset({
        mapSize: { width, height },
        viewportSize,
        positionOrigin,
        scaledPan,
        zoom,
      }),
    [height, positionOrigin, scaledPan, viewportSize, width, zoom],
  );

  const unboundedClientToMap = useCallback(
    (clientCoords: Point): Point => {
      // Measured on use rather than tracked: the viewport moves whenever surrounding panels do.
      const viewportPosition = viewportRef.current?.getBoundingClientRect();
      if (!perspectiveOffset || !viewportPosition) return clientCoords;

      return {
        x: (clientCoords.x - viewportPosition.x + perspectiveOffset.x) / zoom,
        y: (clientCoords.y - viewportPosition.y + perspectiveOffset.y) / zoom,
      };
    },
    [perspectiveOffset, viewportRef, zoom],
  );

  const clientToMap = useCallback(
    (clientCoords: Point): Point => restrictPointToBounds(unboundedClientToMap(clientCoords), moduleMapBounds),
    [moduleMapBounds, unboundedClientToMap],
  );

  // The viewport's own corners in map coordinates - its client position cancels out, leaving the offset.
  const mapBounds = useMemo(() => {
    if (!perspectiveOffset || !viewportSize) return { x: 0, y: 0, width: 0, height: 0 };

    return {
      x: perspectiveOffset.x / zoom,
      y: perspectiveOffset.y / zoom,
      width: viewportSize.width / zoom,
      height: viewportSize.height / zoom,
    };
  }, [perspectiveOffset, viewportSize, zoom]);

  return useMemo(() => ({ clientToMap, mapBounds }), [clientToMap, mapBounds]);
};

export const ConversionProvider = ({ children }: { children: React.ReactNode }) => {
  const conversion = useConversionState();

  return <ConversionContext.Provider value={conversion}>{children}</ConversionContext.Provider>;
};
